export type PeopleDirectoryRow = {
  staffId: number;
  fullName: string;
  email: string | null;
  active: boolean;
  jobTitle: string | null;
  department: string | null;
  managerStaffId: number | null;
  managerName: string | null;
  hiredOn: string | null;
  terminatedOn: string | null;
  openGrantCount: number;
};

export type PeopleDirectorySortKey =
  | "name"
  | "title"
  | "department"
  | "manager"
  | "hired"
  | "terminated"
  | "openApps"
  | "active";

export type PeopleDirectoryStatusFilter = "all" | "active" | "inactive";

function text(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

export function peopleDirectoryStatus(row: PeopleDirectoryRow): "active" | "inactive" {
  if (!row.active || (row.terminatedOn != null && row.terminatedOn !== "")) {
    return "inactive";
  }
  return "active";
}

export function filterPeopleDirectory(
  rows: readonly PeopleDirectoryRow[],
  search: string,
  status: PeopleDirectoryStatusFilter,
): PeopleDirectoryRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (status !== "all" && peopleDirectoryStatus(row) !== status) {
      return false;
    }
    if (needle === "") return true;
    const haystack = [row.fullName, row.email, row.jobTitle, row.department, row.managerName]
      .map((part) => text(part))
      .join(" ");
    return haystack.includes(needle);
  });
}

function compareText(left: string | null, right: string | null): number {
  return text(left).localeCompare(text(right));
}

export function sortPeopleDirectory(
  rows: readonly PeopleDirectoryRow[],
  key: PeopleDirectorySortKey,
  direction: "asc" | "desc",
): PeopleDirectoryRow[] {
  const sorted = [...rows].sort((left, right) => {
    let result = 0;
    if (key === "name") result = compareText(left.fullName, right.fullName);
    else if (key === "title") result = compareText(left.jobTitle, right.jobTitle);
    else if (key === "department") result = compareText(left.department, right.department);
    else if (key === "manager") result = compareText(left.managerName, right.managerName);
    else if (key === "hired") result = compareText(left.hiredOn, right.hiredOn);
    else if (key === "terminated") result = compareText(left.terminatedOn, right.terminatedOn);
    else if (key === "openApps") result = left.openGrantCount - right.openGrantCount;
    else result = Number(left.active) - Number(right.active);
    if (result === 0) result = left.staffId - right.staffId;
    return direction === "asc" ? result : -result;
  });
  return sorted;
}
